import React, { useState, useEffect, useMemo } from 'react';
import { Polygon, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { getLeafletColor } from '../../util/leafletHelpers';
import TractPopup from '../maps/TractPopup';
import AppMap from '../maps/AppMap';
import EditablePolygonGroup from '../maps/EditablePolygonGroup';
import DraggableMarker from '../maps/DraggableMarker';
import './TractMapCard.css';

export default function TractMapCard({
    tractID,
    initialCoords = [],
    onCoordsChange,
    tractInfo = null,
    wells = [],
    allTracts = [],
    uploadRef = null,
    editMode = false,
    downloadPrefix,
    onWellPositionChange,
}) {
    const [coords, setCoords] = useState(initialCoords || []);
    const [map, setMap] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [colorFilters, setColorFilters] = useState(['M', 'S', 'M&S', 'ORRI']);
    const [showWells, setShowWells] = useState(true);

    const updateCoords = (newCoords) => {
        setCoords(newCoords);
        if (onCoordsChange) onCoordsChange(newCoords);
    };

    const handleRestoreSaved = () => {
        // Restore from initialCoords (the last persisted shape from the parent).
        // No onCoordsChange call — the DB already holds this geometry because
        // the polygon-clear path did not propagate to the parent.
        setCoords(initialCoords || []);
    };

    const fitMap = (points) => {
        if (!map) return;
        const latLngs = points.map(([lat, lng]) => L.latLng(lat, lng));
        map.leafletElement.fitBounds(latLngs, { padding: [30, 30], maxZoom: 18 });
    };

    const handleCoordsExtracted = (newCoords) => {
        updateCoords(newCoords);
        fitMap(newCoords);
    };

    useEffect(() => {
        if (coords.length && map) fitMap(coords);
    }, [coords, map]);

    useEffect(() => {
        // Skip when the user is editing — clearing the shape would otherwise
        // trigger this fit-to-all-tracts on the next allTracts cache update,
        // ripping the viewport away from where the user was working.
        if (!map || editMode || coords.length > 0) return;
        const allPositions = [];
        for (const t of allTracts) {
            if (!t.geometry) continue;
            try {
                const pts = JSON.parse(t.geometry).map(([lng, lat]) => [lat, lng]);
                allPositions.push(...pts);
            } catch (e) {}
        }
        if (allPositions.length === 0) return;
        map.leafletElement.fitBounds(
            L.latLngBounds(allPositions.map(([lat, lng]) => L.latLng(lat, lng))),
            { padding: [40, 40] }
        );
    }, [map, allTracts, editMode]);

    const visibleTracts = useMemo(() => allTracts
        .filter(t => t.id !== tractID && t.geometry)
        .filter(t => colorFilters.length === 0 || colorFilters.includes(t.color))
        .filter(t => {
            if (!searchTerm.trim()) return true;
            const term = searchTerm.toLowerCase();
            if (t.id?.toLowerCase().includes(term)) return true;
            if (t.county?.toLowerCase().includes(term)) return true;
            const owners = [
                ...(t.mineralsOwned || []).map(o => o.Owner?.name),
                ...(t.surfaceOwned  || []).map(o => o.Owner?.name),
            ].filter(Boolean).map(n => n.toLowerCase());
            return owners.some(n => n.includes(term));
        }), [allTracts, tractID, colorFilters, searchTerm]);

    useEffect(() => {
        if (!map || !searchTerm.trim() || visibleTracts.length === 0) return;
        const allPositions = [];
        for (const t of visibleTracts) {
            try {
                const pts = JSON.parse(t.geometry).map(([lng, lat]) => [lat, lng]);
                allPositions.push(...pts);
            } catch (e) {}
        }
        if (allPositions.length === 0) return;
        map.leafletElement.fitBounds(
            L.latLngBounds(allPositions.map(([lat, lng]) => L.latLng(lat, lng))),
            { padding: [40, 40] }
        );
    }, [searchTerm, map]);

    function isBadCoord(val) {
        if (val === null || val === undefined) return true;
        const n = parseFloat(val);
        return isNaN(n) || n === 0;
    }

    const allWells = useMemo(() => {
        return (wells || []).filter(w => !isBadCoord(w.latitude) && !isBadCoord(w.longitude));
    }, [wells]);

    const COLOR_TYPES = [
        { label: 'M',   color: '#e53935' },
        { label: 'S',   color: '#1565c0' },
        { label: 'M&S', color: '#c8a800' },
    ];

    const filterBar = (
        <div style={{ padding: '8px 12px', background: '#f0f0f0', borderBottom: '1px solid #ddd', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ flex: 1, minWidth: 160, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', height: 30, boxSizing: 'border-box' }}
            />
            {COLOR_TYPES.map(({ label, color }) => (
                <button
                    key={label}
                    type="button"
                    onClick={() => setColorFilters(prev => prev.includes(label) ? prev.filter(f => f !== label) : [...prev, label])}
                    style={{
                        padding: '4px 10px',
                        borderRadius: 4,
                        border: `2px solid ${color}`,
                        background: colorFilters.includes(label) ? color : '#fff',
                        color: colorFilters.includes(label) ? '#fff' : color,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                    }}
                >
                    {label}
                </button>
            ))}
            <button
                type="button"
                onClick={() => setShowWells(prev => !prev)}
                style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '2px solid #555',
                    background: showWells ? '#555' : '#fff',
                    color: showWells ? '#fff' : '#555',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                }}
            >
                Wells
            </button>
            {editMode && (initialCoords || []).length > 0 && (
                <button
                    type="button"
                    onClick={handleRestoreSaved}
                    title="Restore the polygon to the last saved shape"
                    style={{
                        padding: '4px 10px',
                        borderRadius: 4,
                        border: '2px solid #1976d2',
                        background: '#1976d2',
                        color: '#fff',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                    }}
                >
                    Restore Saved Shape
                </button>
            )}
            <span style={{ marginLeft: 'auto', color: '#555', fontSize: '0.85rem' }}>
                {visibleTracts.length} tract{visibleTracts.length !== 1 ? 's' : ''}
            </span>
        </div>
    );

    return (
        <AppMap
            center={[39.8283, -98.5795]}
            zoom={3}
            uploadRef={uploadRef}
            onCoordsExtracted={handleCoordsExtracted}
            minKmlPoints={3}
            filterBar={filterBar}
            onMapReady={setMap}
            downloadPrefix={downloadPrefix || tractID || 'tract'}
        >
            {/* Other tracts — dimmed, with hover tooltip showing the tract id */}
            {visibleTracts.map(t => {
                try {
                    const positions = JSON.parse(t.geometry).map(([lng, lat]) => [lat, lng]);
                    return (
                        <Polygon key={t.id} positions={positions} color={getLeafletColor(t.color)} weight={1} opacity={0.4} fillOpacity={0.05}>
                            <Tooltip sticky>{t.id}</Tooltip>
                            <TractPopup tract={t} />
                        </Polygon>
                    );
                } catch (e) {
                    return null;
                }
            })}

            {/* Well markers — draggable in edit mode so users can reposition surface coords */}
            {showWells && allWells.map(w => (
                <DraggableMarker
                    key={w.id}
                    position={[parseFloat(w.latitude), parseFloat(w.longitude)]}
                    editMode={editMode}
                    onDragEnd={({ lat, lng }) => onWellPositionChange?.(w, { lat, lng })}
                >
                    <Popup>
                        <Link to={`/Wells/${w.id}?tab=1`}>{w.name}</Link>
                    </Popup>
                </DraggableMarker>
            ))}

            {/* Focal tract polygon — editable in edit mode via the reusable group */}
            <EditablePolygonGroup
                polygons={coords.length > 0 ? [{
                    id: tractID,
                    positions: coords,
                    color: 'black',
                    weight: editMode ? 3 : 4,
                    opacity: editMode ? 0.9 : 1,
                    fillOpacity: editMode ? 0.25 : 0.5,
                    fillColor: getLeafletColor(tractInfo?.color),
                    label: !editMode && tractInfo?.id ? <strong>{tractInfo.id}</strong> : null,
                    labelClassName: 'focal-tract-label',
                    popup: !editMode && tractInfo ? (
                        <Popup>
                            <div>
                                {tractInfo.name && <><u>Tract Info</u><br /></>}
                                {tractInfo.name && <>Name: {tractInfo.name}<br /></>}
                                {tractInfo.state && <>State: {tractInfo.state}<br /></>}
                                {tractInfo.county && <>County: {tractInfo.county}<br /></>}
                                {tractInfo.acreage && <>Acreage: {tractInfo.acreage}<br /></>}
                                {tractInfo.grossMineralAcres && <>Gross Mineral Acres: {tractInfo.grossMineralAcres}<br /></>}
                                {tractInfo.grossSurfaceAcres && <>Gross Surface Acres: {tractInfo.grossSurfaceAcres}<br /></>}
                                {tractInfo.mineralFraction && <>Mineral Fraction: {tractInfo.mineralFraction}<br /></>}
                                {tractInfo.familyNMA && <>Family NMA: {tractInfo.familyNMA}<br /></>}
                                {tractInfo.survey && <>Survey: {tractInfo.survey}<br /></>}
                                {tractInfo.abstract && <>Abstract: {tractInfo.abstract}<br /></>}
                                {tractInfo.legalDescription && <>Legal Description: {tractInfo.legalDescription.length > 100 ? tractInfo.legalDescription.substring(0, 100) + '...' : tractInfo.legalDescription}<br /></>}
                            </div>
                        </Popup>
                    ) : null,
                }] : []}
                editMode={editMode}
                allowDraw={editMode && coords.length === 0}
                allowRemove
                onEdit={(id, newCoords) => updateCoords(newCoords)}
                onCreate={(newCoords) => updateCoords(newCoords)}
                onClear={() => setCoords([])}
            />
        </AppMap>
    );
}
